/**
 * Search-snippet helpers shared by the prerender (scripts/seo-meta.mjs, via the
 * scripts/seo-snippets.mjs esbuild loader) and the client head (src/utils/blogSeo.js),
 * so both ship the same <title> and description (docs/SEO_SNIPPET_LIMITS_1028.md).
 * Google cuts titles near 60 characters and descriptions near 160.
 */
export const SEO_TITLE_MAX = 60;
export const SEO_DESCRIPTION_MAX = 160;
export const SITE_BRAND = 'CFA Mock Exam';
export const STUDY_GUIDE_BRAND = 'CFA Level 1 Study Guide';

/** First of `title | suffix…` (longest suffix first) that fits in max; else the bare title. */
export function documentTitle(title, suffixes = [SITE_BRAND], max = SEO_TITLE_MAX) {
  const list = Array.isArray(suffixes) ? suffixes : [suffixes];
  for (const suffix of list) {
    const full = `${title} | ${suffix}`;
    if (full.length <= max) return full;
  }
  return title;
}

/** Blog post <title>: "Post | CFA Mock Exam" when it fits, else the post title. */
export function blogPostDocumentTitle(postTitle) {
  return documentTitle(postTitle);
}

/** Study-guide topic/lesson <title>, dropping brand parts until it fits. */
export function studyGuideDocumentTitle(pageTitle) {
  return documentTitle(pageTitle, [
    `${STUDY_GUIDE_BRAND} | ${SITE_BRAND}`,
    STUDY_GUIDE_BRAND,
    SITE_BRAND,
  ]);
}

/** Default topic/lesson description (no lead on the note). */
export function studyGuideDescription(pageTitle) {
  return `CFA Level 1 study guide: ${pageTitle}. 2026 curriculum notes with links to topic drills, the formula glossary and free Mock Exam 1.`;
}

export const STUDY_GUIDE_HUB_TITLE = `CFA Level 1 Study Guide (2026) | ${SITE_BRAND}`;
export const STUDY_GUIDE_HUB_DESCRIPTION =
  'Free CFA Level 1 study guide at 2026 topic weights: ethics, quant, economics, FRA, corporate issuers, equity, fixed income, derivatives, alternatives and PM.';

export const STUDY_PLAN_TITLE = `CFA Level 1 Study Plan Generator (Free) | ${SITE_BRAND}`;
export const STUDY_PLAN_DESCRIPTION =
  'Free CFA Level 1 study plan generator: enter your mock scores and exam date for a week-by-week plan at 2026 topic weights, with Mocks 2–4 as checkpoints.';

/** /level/2 and /level/3 hubs; /quiz/l2 and /quiz/l3 pickers (distinct titles and copy). */
export const LEVEL_HUB_SEO = {
  l2: {
    title: `CFA Level 2 Mock Exams 2026 | Free Mock 1 | ${SITE_BRAND}`,
    description:
      'Timed CFA Level 2 mock exams: four 92-question item-set forms at 2026 topic weights. Mock 1 is free (score only); lifetime access or $29/mo unlocks the rest.',
  },
  l3: {
    title: `CFA Level 3 Mock Exams 2026 | Free Mock 1 | ${SITE_BRAND}`,
    description:
      'Timed CFA Level 3 mock exams: four 132-minute sessions mixing item sets and constructed response. Mock 1 is free (score only); lifetime or $29/mo unlocks all.',
  },
};

export const LEVEL_PICKER_SEO = {
  l2: {
    title: `Pick a CFA Level 2 Mock Exam | Free Mock 1 | ${SITE_BRAND}`,
    description:
      'Choose your CFA Level 2 mock exam: start free Mock 1 (score only), then unlock Mocks 2–4, timed item-set forms at 2026 topic weights, lifetime or monthly.',
  },
  l3: {
    title: `Pick a CFA Level 3 Mock Exam | Free Mock 1 | ${SITE_BRAND}`,
    description:
      'Choose your CFA Level 3 mock exam: start free Mock 1 (score only), then unlock Mocks 2–4, timed mixed item-set and constructed-response sessions.',
  },
};

/** All-level catalogue, distinct from the Level 1 session picker. */
export const MOCK_EXAMS_HUB_SEO = {
  path: '/cfa-mock-exams',
  title: 'CFA Mock Exams by Level: Free & Paid | CFA Mock Exam',
  description: 'Browse CFA mock exams for Levels 1, 2 and 3. Compare questions, timing and free Mock 1 access, then choose a timed session and review your score.',
  h1: 'CFA Mock Exams',
  intro: 'Choose a CFA Mock Exam by level. Whether you call it a mock test or a mock exam, use a timed sitting to practise pacing and check your progress.',
};
